import { Box, CircularProgress } from '@mui/material'
import { Auth, Hub } from 'aws-amplify'
import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Identity_Type_Enum,
  InsertIdentityMutation,
  InsertIdentityMutationVariables,
  SyncSsoProfileMutation,
  SyncSsoProfileMutationVariables,
} from '@app/generated/graphql'
import {
  INSERT_IDENTITY_MUTATION,
  SYNC_SSO_PROFILE_MUTATION,
} from '../../queries/complete-profile'

import { isFullUrl } from '@app/util'
import { useAuth } from '@app/context/auth'
import { useResolveSSOProfile } from '../../hooks/useResolveSSOProfile'
import { useCreateStubProfile } from '../../hooks/useCreateStubProfile'
import { useMutation } from 'urql'

// Mirrors `doNotRedirectToOnboardingPathnames` in src/routes/index.tsx -
// these deep links intentionally defer profile completion until after
// the user has acted on the invite.
const inviteAcceptPathnames = ['accept-invite', 'accept-org-invite']

/**
 * Landing route for the Cognito Hosted UI (Managed Login / SSO) redirect.
 * Amplify's `Hub.listen('auth', ...)` wiring in AuthProvider completes the
 * sign-in; this page then bridges the Cognito identity to Connect (via the
 * shared `useResolveSSOProfile` hook) and routes onward - to
 * `/complete-profile` (new or incomplete profiles) or the original
 * destination. Incomplete profiles heading to an invite are an exception -
 * accept/decline happens first, profile completion is deferred until after.
 */
export const SsoCallback: FC = () => {
  const navigate = useNavigate()
  const { reloadCurrentProfile } = useAuth()
  const [redirectTo, setRedirectTo] = useState<string>()
  const [, syncSsoProfile] = useMutation<
    SyncSsoProfileMutation,
    SyncSsoProfileMutationVariables
  >(SYNC_SSO_PROFILE_MUTATION)
  const [, insertIdentity] = useMutation<
    InsertIdentityMutation,
    InsertIdentityMutationVariables
  >(INSERT_IDENTITY_MUTATION)
  const [, createStubProfile] = useCreateStubProfile()

  // `federatedSignIn`'s `customState` (set by ManagedLoginRedirect) is
  // delivered via a separate `customOAuthState` Hub event, dispatched
  // synchronously right after `cognitoHostedUI`.
  useEffect(() => {
    return Hub.listen('auth', ({ payload }) => {
      if (payload.event !== 'customOAuthState') return

      try {
        const { redirectTo: parsedRedirectTo } = JSON.parse(
          payload.data as string,
        ) as { redirectTo?: string }
        if (parsedRedirectTo) setRedirectTo(parsedRedirectTo)
      } catch {
        // Malformed/missing custom state - fall back to the default redirect.
      }
    })
  }, [])

  const {
    cognitoIdentity,
    connectProfile,
    ssoProfile,
    isProfileComplete,
    fetching,
    error,
  } = useResolveSSOProfile()

  const completeSignIn = useCallback(async () => {
    if (connectProfile?.id && !ssoProfile?.id && cognitoIdentity) {
      await insertIdentity({
        input: {
          id: cognitoIdentity.id,
          provider_id: cognitoIdentity?.id,
          profile_id: connectProfile.id,
          type: Identity_Type_Enum.SsoCognito,
        },
      })
      const { data } = await syncSsoProfile({
        input: { profileId: connectProfile.id },
      })
      if (data?.syncSsoProfile?.status === 200) {
        const currentUser = await Auth.currentUserPoolUser()
        await currentUser.refreshSessionIfPossible()
      }
    }

    if (redirectTo && isFullUrl(redirectTo)) {
      window.location.href = redirectTo
      return
    }
    navigate(redirectTo || '/', { replace: true })
  }, [
    connectProfile?.id,
    ssoProfile?.id,
    syncSsoProfile,
    redirectTo,
    navigate,
    cognitoIdentity,
    insertIdentity,
  ])

  // For a brand-new identity there's no profile row yet (and therefore no
  // auth.profile) to land on the invite page with, so one has to be
  // created first - the `register` action handles identity linking itself
  // for this case, unlike `completeSignIn`'s path for pre-existing profiles.
  const createProfileAndContinueToInvite = useCallback(async () => {
    if (!cognitoIdentity) return

    const result = await createStubProfile(cognitoIdentity)
    if (result.error) {
      navigate('/complete-profile', { replace: true, state: { redirectTo } })
      return
    }

    await reloadCurrentProfile()

    if (redirectTo && isFullUrl(redirectTo)) {
      window.location.href = redirectTo
      return
    }
    navigate(redirectTo || '/', { replace: true })
  }, [
    cognitoIdentity,
    createStubProfile,
    navigate,
    redirectTo,
    reloadCurrentProfile,
  ])

  useEffect(() => {
    if (!cognitoIdentity || fetching) return

    if (error) {
      navigate('/login', { replace: true })
      return
    }

    const isInviteRedirect =
      !!redirectTo &&
      !isFullUrl(redirectTo) &&
      inviteAcceptPathnames.some(path => redirectTo.includes(path))

    // Complete profiles can just sign in; incomplete ones only skip
    // the complete-profile redirect when accepting/declining an invite.
    if (isProfileComplete || (isInviteRedirect && connectProfile?.id)) {
      completeSignIn()
      return
    }

    if (isInviteRedirect) {
      createProfileAndContinueToInvite()
      return
    }

    navigate('/complete-profile', { replace: true, state: { redirectTo } })
  }, [
    cognitoIdentity,
    fetching,
    error,
    isProfileComplete,
    redirectTo,
    navigate,
    connectProfile?.id,
    completeSignIn,
    createProfileAndContinueToInvite,
  ])

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress size={60} />
    </Box>
  )
}
